import axios, { AxiosError } from 'axios'
import { getApiLocaleHeaders } from '@/core/api/locale-headers'
import { installInFlightGetDedupe } from '@/core/api/in-flight-get-dedupe'
import { isDeviceOnline } from '@/shared/composables/use-network-status'

/**
 * Browser API base. Dev + Docker prod: relative /api/v1 (Vite proxy or nginx) —
 * works when opening the app from a phone on LAN/ngrok. Never call localhost:8080 from the phone.
 */
function resolveApiBase(): string {
  const configured = (import.meta.env.VITE_API_BASE as string | undefined)?.trim()
  const fallback = '/api/v1'

  if (import.meta.env.DEV) {
    return fallback
  }

  if (!configured || configured.startsWith('/')) {
    return configured || fallback
  }

  if (typeof window !== 'undefined') {
    try {
      const apiOrigin = new URL(configured).origin
      if (apiOrigin !== window.location.origin) {
        return fallback
      }
    } catch {
      return fallback
    }
  }

  return configured
}

export const API_BASE = resolveApiBase()

/** Match authenticated client: fail after 30s unless the call overrides `timeout`. */
export const API_REQUEST_TIMEOUT_MS = 30_000

export const bareHttp = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: API_REQUEST_TIMEOUT_MS,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

installInFlightGetDedupe(bareHttp)

bareHttp.interceptors.request.use((config) => {
  if (!isDeviceOnline()) {
    return Promise.reject(new AxiosError('Network Error', 'ERR_NETWORK', config))
  }
  Object.assign(config.headers, getApiLocaleHeaders())
  if ((config.method ?? 'get').toLowerCase() === 'get') {
    config.headers['Cache-Control'] = 'no-cache'
    config.headers.Pragma = 'no-cache'
    config.headers.Expires = '0'
  }
  return config
})
